import React, { FC, useState } from "react";

import { useTranslation } from "react-i18next";
import { Collapse, Table, TableBody, TableCell } from "@mui/material";

import { RafterDownIcon } from "@/assets/icons";
import { MaxLinesCell } from "@/components/molecules";
import { InsuredGood, Permissions } from "@/shared/domain";
import { useAuth, useNavigation } from "@/shared/hooks";
import { StyledTableCell, StyledTableCellText, StyledTableRow } from "@/styles/styled";

import { insuredGoodTableColumns } from "../utils";
import { StyledTableExpandableCell, StyledTableExpandedRow } from "./styled";

export type InsuredGoodTableItemProps = {
    insuredGood: InsuredGood;
};

const InsuredGoodTableItem: FC<InsuredGoodTableItemProps> = ({ insuredGood }) => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const { navToInsuredGoodDetail, navToContractDetail } = useNavigation();
    const [open, setOpen] = useState(false);
    const canViewDetails = user.hasPermission(Permissions.ViewRisksDetails, insuredGood.policyholderId);

    const handleInsuredGoodDetails = () => {
        return canViewDetails && navToInsuredGoodDetail(insuredGood);
    };

    const hasLinkedContracts = !!insuredGood?.relatedContracts?.length;

    const canViewLinkedContractDetails = user.hasPermission(
        Permissions.ViewContractsDetails,
        insuredGood.policyholderId,
    );

    return (
        <>
            <StyledTableRow
                hover={canViewDetails}
                onClick={handleInsuredGoodDetails}
                data-testid={`insured_good-${insuredGood.id}`}
            >
                <MaxLinesCell testId="table-cell-type" maxLines={3} variant="main" highlight>
                    {insuredGood.type.name}
                </MaxLinesCell>
                <MaxLinesCell testId="table-cell-title" maxLines={3} highlight>
                    {insuredGood.title}
                </MaxLinesCell>
                {hasLinkedContracts ? (
                    <StyledTableExpandableCell
                        data-testid="table-cell-linked_contract_expand"
                        $noEllipsis
                        aria-label="expand row"
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setOpen(!open);
                        }}
                    >
                        <StyledTableCellText>{t("Common.SeeContracts")}</StyledTableCellText>
                        <RafterDownIcon className="arrowIcon" style={open ? null : { transform: "rotate(180deg)" }} />
                    </StyledTableExpandableCell>
                ) : (
                    <TableCell />
                )}
            </StyledTableRow>
            {open && (
                <StyledTableRow>
                    <TableCell colSpan={3} padding="none">
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Table
                                size="small"
                                aria-label="expanded linked contracts table"
                                style={{ tableLayout: "fixed" }}
                            >
                                <colgroup>
                                    {insuredGoodTableColumns.map((column) => (
                                        <col
                                            width={column.width}
                                            key={`table-col-${column.sortableKey || column.unsortableKey}`}
                                        />
                                    ))}
                                </colgroup>
                                <TableBody>
                                    {insuredGood.relatedContracts.map((contract) => (
                                        <StyledTableExpandedRow
                                            key={contract.id}
                                            data-testid={`linked-contract-${contract.id}`}
                                            hover={canViewLinkedContractDetails}
                                            onClick={
                                                canViewLinkedContractDetails && (() => navToContractDetail(contract))
                                            }
                                        >
                                            <StyledTableCell style={{ paddingLeft: "40px" }}>
                                                <StyledTableCellText>{contract.branch.name}</StyledTableCellText>
                                            </StyledTableCell>
                                            <StyledTableCell align="left">{contract.reference}</StyledTableCell>
                                            <TableCell />
                                        </StyledTableExpandedRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Collapse>
                    </TableCell>
                </StyledTableRow>
            )}
        </>
    );
};

export default InsuredGoodTableItem;
